import { flatten, pick } from 'lodash';
import { isAxiosError } from 'axios';
import { ApiError, AppError, ErrorCode } from '@/types/ErrorCodes';
import { formatDistance } from 'date-fns';
import { BaseEntity } from '@/api/EntityApi';
import { Language, Translations } from '@/types/types';
import {
	PredefinedDimension,
	ProductType,
	SectionType,
} from '@/api/ProductTypeApi';
import { pl } from 'date-fns/locale';
import { QuestionType } from '@/api/FormTemplateApi';
import { Permissions } from '@/api/RoleApi';
import APP_CONFIG from '@/configs/appConfig';
import LANGUAGES from '@/configs/languages';
import moment from 'moment';
import { FormStatus } from '@/api/FormApi';
import { UserStatus } from '@/types/users';
import { ProjectStatus } from '@/api/ProjectsApi';
import { OrderStatus } from '@/api/OrdersApi';
import { AttributeViewType } from '@/api/AttributeApi';

export const priceFormatter = new Intl.NumberFormat('PL-pl', {
	style: 'currency',
	currency: 'PLN',
	currencyDisplay: 'code',
});

const PASSWORD_REGEX = /^(?=\D*\d)[^ ]{6,}$/;

/**
 *
 * @param password one number and at least 8
 * @returns
 */
export function validatePassword(password: string) {
	return PASSWORD_REGEX.test(password);
}

export function randomDate(start: Date, end: Date) {
	return moment(
		start.getTime() + Math.random() * (end.getTime() - start.getTime())
	).toDate();
}

export function formatDateForApi(date: moment.MomentInput) {
	return moment(date).format('YYYY-MM-DD');
}

export function formatDateForPresentation(
	date: moment.MomentInput = new Date()
) {
	return moment(date).format('DD/MM/YYYY');
}

export function formatTimeForPresentation(
	date: moment.MomentInput = new Date()
) {
	return moment(date).format('HH:mm');
}

export function sortByDate(
	dateA: moment.MomentInput,
	dateB: moment.MomentInput,
	order: 'asc' | 'desc' = 'asc'
) {
	const first = moment(dateA).valueOf(),
		second = moment(dateB).valueOf();

	return order === 'asc' ? first - second : second - first;
}

export function sortByName(
	nameA = '',
	nameB = '',
	order: 'asc' | 'desc' = 'asc'
) {
	return order === 'asc'
		? nameA.localeCompare(nameB)
		: nameB.localeCompare(nameA);
}

export function incrementDate(date: moment.MomentInput) {
	return moment(date).add(1, 'day').toDate();
}

export function decrementDate(date: moment.MomentInput) {
	return moment(date).subtract(1, 'day').toDate();
}

export function handleUnload(e: BeforeUnloadEvent) {
	e.preventDefault();
	e.returnValue = '';
}

export const ID_REGEX =
	/[a-f0-9]{8}-?[a-f0-9]{4}-?[a-f0-9]{4}-?[89ab][a-f0-9]{3}-?[a-f0-9]{12}/g;

export function getIdsFromText(text: string) {
	return [...new Set(flatten([...text.matchAll(ID_REGEX)]))];
}

export function fileTypeToHeader(fileType: 'csv' | 'xlsx' | 'pdf') {
	switch (fileType) {
		case 'xlsx':
			return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
		case 'csv':
			return 'text/csv';
		case 'pdf':
			return 'application/pdf';
		default:
			return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
	}
}

export function priceToFixed(price: number) {
	return price.toFixed(2);
}

export const formatPrice = (value = 0, opts?: Intl.NumberFormatOptions) => {
	const formatter = new Intl.NumberFormat('pl', {
		style: 'currency',
		currency: 'PLN',
		currencyDisplay: 'code',
		maximumFractionDigits: !value ? 0 : undefined,
		...opts,
	});

	return formatter.format(value);
};

export const isAppError = (error: unknown): error is AppError => {
	return (
		!!(error as AppError).status &&
		!!(error as AppError).stack &&
		!!(error as AppError).message
	);
};

export const getErrorCodes = (error: unknown) => {
	if (isAxiosError<ApiError>(error) && error.response)
		return Array.isArray(error.response.data.message)
			? error.response.data.message
			: [error.response.data.message];

	return [ErrorCode.GENERIC];
};

export const errorCodesToMessage = (errorCodes: ErrorCode[]) =>
	errorCodes.map((code) => `errors.${code}`).join(', ');

export const fontColorForBackground = (backgroundColorHex = '#ffffff') => {
	return [
		0.299 * parseInt(backgroundColorHex.slice(1, 3), 16),
		0.587 * parseInt(backgroundColorHex.slice(3, 5), 16),
		0.114 * parseInt(backgroundColorHex.slice(5, 7), 16),
	].reduce((a, b) => a + b) /
		255 >
		0.65
		? '#495057'
		: '#ffffff';
};

export const formatFullName = (firstName?: string, lastName?: string) => {
	if (firstName && lastName) return `${firstName} ${lastName}`;
	if (!firstName && !lastName) return `-`;
	return firstName || lastName;
};

export function formatDateFromNow(date: Date) {
	return formatDistance(date, new Date(), {
		locale: pl,
		includeSeconds: true,
		addSuffix: true,
	});
}

export function isDefaultLanguage(language: Language): language is 'PL' {
	return language === APP_CONFIG.DEFAULT_LANGUAGE;
}

export function fileSizeToMB(size: number | string) {
	return Math.round((Number(size) / 1_000_000 + Number.EPSILON) * 100) / 100;
}

export function extractId<TData extends BaseEntity>(entity: TData) {
	return pick(entity, 'id');
}

export function mapQuestionTypesToNames(type: QuestionType) {
	switch (type) {
		case QuestionType.SELECT:
			return 'Select';
		case QuestionType.FILES:
			return 'Pliki';
		case QuestionType.MODAL_PICKER:
			return 'Modal';
		case QuestionType.NUMBER:
			return 'Liczba';
		case QuestionType.TEXT:
			return 'Tekst';
		case QuestionType.TEXTAREA:
			return 'Textarea';
		case QuestionType.DIMENSIONS:
			return 'Wymiary';
		case QuestionType.ADVANCED_OPTIONS:
			return 'Zaawansowane opcje';
		case QuestionType.MODEL_PICKER_TYPE:
			return 'Typ modelu';
	}
}

export function getFileSrc(path?: string) {
	return `${import.meta.env.VITE_API_URL_UPLOADS}${path}`;
}

export function formatExactDate(date: moment.MomentInput = new Date()) {
	return `${formatDateForPresentation(date)}, ${formatTimeForPresentation(
		date
	)}`;
}

export function blankProductTypeContent(): ProductType['content'] {
	return {
		[SectionType.CARDS]: {
			title: '',
			description: '',
			cards: [],
		},
		[SectionType.LISTS]: {
			title: '',
			description: '',
			lists: [],
		},
		[SectionType.ARTICLES]: {
			title: '',
			description: '',
			articles: [],
		},
	};
}

export function formatPermisionName(section: keyof Permissions) {
	switch (section) {
		case 'admins':
			return 'Admini';
		case 'roles':
			return 'Grupy dostępu';
		case 'formTemplates':
			return 'Szablony formularzy';
		case 'globalSettings':
			return 'Ustawienia globalne';
		case 'productTypes':
			return 'Typy produktów';
		case 'forms':
			return 'Zapytania ofertowe';
	}
}

export function formatUserStatusName(userStatus: UserStatus) {
	switch (userStatus) {
		case UserStatus.ACTIVE:
			return 'Aktywne';
	}
}

export function formatFormStatus(status: FormStatus) {
	const data = { color: '', name: '' };

	switch (status) {
		case FormStatus.NEW_FORM:
			data.color = 'warning';
			data.name = 'Nowe zapytanie';
			break;
		case FormStatus.IN_VALUATION:
			data.color = 'info';
			data.name = 'Wyceniono';
			break;
		case FormStatus.ESTIMATED:
			data.color = 'secondary';
			data.name = 'Wycena zaakceptowana';
			break;
		case FormStatus.IN_PRODUCTION:
			data.color = 'primary';
			data.name = 'W produkcji';
			break;
		case FormStatus.COMPLETED:
			data.color = 'success';
			data.name = 'Zrealizowane';
			break;
	}

	return data;
}

export function formatProjectStatus(status: ProjectStatus) {
	const data = { color: '', name: '' };

	switch (status) {
		case ProjectStatus.NEW:
			data.color = 'warning';
			data.name = 'Nowe zapytanie';
			break;
		case ProjectStatus.IN_VALUATION:
			data.color = 'info';
			data.name = 'W trakcie wyceny';
			break;
		case ProjectStatus.READY_TO_ORDER:
			data.color = 'info';
			data.name = 'Gotowe do zamówienia';
			break;
		case ProjectStatus.IN_PROGRESS:
			data.color = 'primary';
			data.name = 'W produkcji';
			break;
		case ProjectStatus.COMPLETED:
			data.color = 'success';
			data.name = 'Zrealizowane';
			break;
	}

	return data;
}

export const getDimensionTitle = (dimension: PredefinedDimension) => {
	return `${dimension.height}mm x ${dimension.width}mm x ${dimension.depth}mm`;
};

export function formatOrderStatus(status: OrderStatus) {
	let color: string;
	let name: string;

	switch (status) {
		case OrderStatus.NEW:
			color = 'info';
			name = 'Nowe';
			break;
		case OrderStatus.IN_PROGRESS:
			color = 'warning';
			name = 'W realizacji';
			break;
		case OrderStatus.READY:
			color = 'primary';
			name = 'Gotowe';
			break;
		case OrderStatus.COMPLETED:
			color = 'success';
			name = 'Zakończone';
			break;
		case OrderStatus.CANCELLED:
			color = 'danger';
			name = 'Anulowane';
			break;
		default:
			color = 'secondary';
			name = 'Nieznany';
	}

	return { color, name };
}

export function getAttributeViewTypeName(viewType: AttributeViewType) {
	switch (viewType) {
		case AttributeViewType.TILES:
			return 'Kafelki';
		case AttributeViewType.SELECT:
			return 'Lista rozwijana';
		case AttributeViewType.CHIP_SINGLE:
			return 'Wybór (chip)';
		case AttributeViewType.OPTION_GRID:
			return 'Siatka opcji';
		case AttributeViewType.NUMBER:
			return 'Pole liczbowe';
		case AttributeViewType.TEXTAREA:
			return 'Pole tekstowe';
		default:
			return viewType;
	}
}

export function getEmptyTranslationObject<K extends string>(
	keys: K[]
): Translations<Record<K, string>> {
	const translations = {} as Translations<Record<K, string>>;

	let language: Language;
	for (language in LANGUAGES) {
		if (language === APP_CONFIG.DEFAULT_LANGUAGE) continue;

		translations[language as Exclude<Language, 'PL'>] =
			{} as Record<K, string>;

		for (const key of keys) {
			translations[language as Exclude<Language, 'PL'>][key] = '';
		}
	}

	return translations;
}
